// The furniture around the picture: the transport row under the video and the
// title block under that. Both players compose it, so the overlay and the
// standalone page cannot drift apart again.

import React, { useState } from 'react';
import { Box, Menu, MenuItem, Typography } from '@mui/material';
import ClosedCaptionIcon from '@mui/icons-material/ClosedCaption';
import Chapters from './Chapters';
import { parseProducts } from '../ps/format';

// A button shows its state, not its name: the language when subtitles are on,
// OFF when they are not. "Subtitles: OFF" spent 110 pixels saying what the
// chip says in three characters -- but it has to be readable at a glance, so
// the glyph is 19px and the unlit state still carries a word.
function SubtitleChip({ subtitles, selected, onSelect }) {
  const [anchorEl, setAnchorEl] = useState(null);

  if (!subtitles || subtitles.length === 0) return null;

  const on = Boolean(selected);

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-label={on ? `Subtitles: ${selected.label}` : 'Subtitles off'}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          flexShrink: 0,
          height: 26,
          px: '9px',
          borderRadius: '5px',
          cursor: 'pointer',
          font: 'inherit',
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: '0.09em',
          transition: 'color 130ms cubic-bezier(0.22, 1, 0.36, 1), border-color 130ms cubic-bezier(0.22, 1, 0.36, 1), background 130ms cubic-bezier(0.22, 1, 0.36, 1)',
          ...(on
            ? {
                background: 'color-mix(in srgb, var(--ps-gold) 16%, transparent)',
                border: '1px solid color-mix(in srgb, var(--ps-gold) 90%, transparent)',
                color: 'var(--ps-gold)',
              }
            : {
                backgroundColor: 'var(--ps-surface)',
                backgroundImage: 'linear-gradient(var(--ps-surface-top), var(--ps-surface))',
                border: '1px solid var(--ps-line)',
                boxShadow: 'inset 0 1px 0 var(--ps-border-hi)',
                color: 'var(--ps-text-2)',
                '&:hover': { color: 'var(--ps-text)', borderColor: 'var(--ps-surface-hi)' },
              }),
        }}
      >
        <ClosedCaptionIcon sx={{ fontSize: 19 }} />
        {on ? (selected.srclang || '').toUpperCase() : 'OFF'}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <MenuItem selected={!on} onClick={() => { setAnchorEl(null); onSelect(null); }}>
          Off
        </MenuItem>
        {subtitles.map((subtitle) => (
          <MenuItem
            key={subtitle.srclang}
            selected={on && selected.srclang === subtitle.srclang}
            onClick={() => { setAnchorEl(null); onSelect(subtitle); }}
          >
            {subtitle.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export function PlayerBar({
  subtitles,
  selectedSubtitle,
  onSubtitleSelect,
  chapters,
  currentTime,
  duration,
  onChapterClick,
}) {
  const hasChapters = Array.isArray(chapters) && chapters.length > 0;
  if (!subtitles?.length && !hasChapters) return null;

  return (
    <div className="learn-player__bar">
      <SubtitleChip subtitles={subtitles} selected={selectedSubtitle} onSelect={onSubtitleSelect} />
      {hasChapters ? (
        <Chapters
          chapters={chapters}
          currentTime={currentTime}
          duration={duration}
          onChapterClick={onChapterClick}
        />
      ) : (
        <Box sx={{ flex: 1 }} />
      )}
    </div>
  );
}

// Title, then what it is, then what it is about. The category and the products
// are captions; the title is the only thing here in the reading voice.
export function TitleBlock({ video }) {
  if (!video) return null;
  const products = parseProducts(video.products);

  return (
    <Box sx={{ mt: '22px', textAlign: 'left' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '6px', flexWrap: 'wrap' }}>
        {video.category && <span className="ps-label">{video.category}</span>}
        {products.map((product) => (
          <span key={product} className="ps-label ps-label--dim">
            {product}
          </span>
        ))}
      </Box>

      {video.title && (
        <Typography sx={{ fontSize: { xs: 17, sm: 20 }, fontWeight: 500, color: 'var(--ps-text)', lineHeight: 1.2 }}>
          {video.title}
        </Typography>
      )}

      {video.description && (
        <Typography sx={{ mt: '10px', fontSize: 13, lineHeight: 1.55, color: 'var(--ps-text-2)', maxWidth: '72ch' }}>
          {video.description}
        </Typography>
      )}
    </Box>
  );
}

export default PlayerBar;
